import React, { RefObject } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import { APIError, Category, Department, FormFieldConfig, searchQueryParams } from "../helpers/shared-interfaces";
import Table, { ColumnType } from "antd/lib/table";
import debounce from "lodash.debounce";
import queryString from "query-string";
import { generateDisplayErrorMessage, handleValidationErrors, mapAPIErrorToFormError, request, responseHandler } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { Button, Col, FormInstance, Row, message } from "antd";
import Card from "../components/Card";
import { PlusOutlined } from "@ant-design/icons";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { CREATE_CATEGORY, GET_ADMIN_CATEGORIES } from "../gql/categories";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { PageToolbar } from "../components/PageToolbar";
import { GET_DEPARTMENTS } from "../gql/departments";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { extractPageFromQuery } from "../components/TableFilters";


interface State {
  isLoading: boolean;
  categories: Category[];
  page: number;
  pageSize: number;
  q: string;
  categoriesCount: number;
  departments: Department[];
  showNewCategoryModal: boolean;
  isCreatingCategory: boolean;
  id?: string;
}

export function getCategoriesColumn<T extends Category>(
  categories: Category[],
): ColumnType<T>[] {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Department",
      dataIndex: "Department",
      key: "department",
      render(text: string, record: Category): JSX.Element {
        return (
          <NavLink to={`/departments/${record?.department.id}`}>
            {record?.department.name}
          </NavLink>
        );
      },
    },
    {
      title: "Is Active?",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      render(text: string, record: Category): JSX.Element {
        return <span>{record.isActive.toString()}</span>;
      },
    },
  ];
}

export const NewCategoryUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isCreatingCategory: boolean;
  departments: Department[];
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      name: "name",
      rule: "string",
      inputType: "string",
      required: true,
      label: "Name",
      placeholder: "Name",
      colConfig: {
        span: 24,
      },
    },
    {
      name: "departmentId",
      rule: "string",
      inputType: "select",
      required: false,
      label: "Department",
      placeholder: "Department",
      selectOptions: props.departments,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <BaseForm
      layout="vertical"
      reference={props.reference}
      fieldsToHide={[]}
      fields={fields}
    />
  );
};

export default class Categories extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      categories: [],
      categoriesCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      showNewCategoryModal: false,
      isCreatingCategory: false,
      departments: []
    };
    this.reference = React.createRef<FormInstance>();
    this.toggleCreateCategoryModal =
      this.toggleCreateCategoryModal.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.createCategory = this.createCategory.bind(this);
    this.getAllDepartments = this.getAllDepartments.bind(this);
  }

  toggleCreateCategoryModal(): void {
    this.setState((prevState) => ({
      showNewCategoryModal: !prevState.showNewCategoryModal
    }))
  }

  async componentDidMount(): Promise<void> {
    this.getAllDepartments()
    this.mergeWithLocalState();
    await this.triggerImmediateSearch();
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.triggerImmediateSearch();
      },
    );
  }

  handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ q: event.target.value, page: 1 }, () =>
      this.triggerDelayedSearch(),
    );
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data: { data } }: any = await request({
        query: GET_ADMIN_CATEGORIES,
        variables: { page, q },
      });
      
      this.syncBrowserQueryString();
      this.setState({
        categories: data?.adminCategories?.results,
        categoriesCount: data?.adminCategories?.count,
      });
    } catch (error) {
      message.error(`Failed To Load Categories - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  extractQueryParamsFromUrl = (): searchQueryParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
  };

  mergeWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }

    if (parsed.page) {
      page = extractPageFromQuery(parsed.page);
    }

    this.setState({
      q,
      page,
    });
  };

  syncBrowserQueryString = (): void => {
    const { q, page } = this.state;
    const queryStrings = [];
    if (q) {
      queryStrings.push(`q=${q}`);
    }
    queryStrings.push(`page=${page}`);

    const queryString = `?${queryStrings.join("&")}`;

    this.props.history.replace(queryString);
  };

  redirectToViewCategoryPage(): void {
    this.props.history.push(`/categories/${this.state.id}`);
  }

  getAllDepartments = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: GET_DEPARTMENTS,
      });
      this.setState({
        departments: data.departments,
      });
    } catch (error) {
      // Fail silently
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async createCategory(): Promise<void> {
    interface Response {
      category: Category;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "name",
        "departmentId",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_CATEGORY,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createCategory",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.category?.id }, () => {
          message.success("Category created sucessfully");
          this.redirectToViewCategoryPage();
        });
      }
    } catch (error) {
      message.error(`Failed to create category - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleCreateCategoryModal()
    }
  }

  render(): JSX.Element | null {
    const { isLoading, q, categories, categoriesCount, page, pageSize, showNewCategoryModal, isCreatingCategory, departments } =
      this.state;

    const resultsWithKeys: Category[] = categories.map(
      (result: Category): Category => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
      <>
        <Card>
        <div className="categories-container">
          <PageToolbar modelName="categories"
            searchValue={q}
            onSearchChange={this.handleSearchInput}
            searchPlaceholder="Search by name"
            primaryAction={
              <EntitledSection entitlement={entitlements.TransferRequests.create}>
                <Button
                  type="primary"
                  onClick={this.toggleCreateCategoryModal}
                  data-testid="create-category-button"
                >
                  <PlusOutlined /> Create Category
                </Button>
              </EntitledSection>
            }
            totalCount={categoriesCount}
            currentDataCount={categories.length}
            page={page}
            pageSize={pageSize}
          />

          <Row>
            <Col span={24}>
              <Table
                loading={isLoading}
                scroll={{ x: 400 }}
                columns={getCategoriesColumn(categories)}
                dataSource={resultsWithKeys}
                onRow={(record) => ({
                  onClick: () => {
                    this.props.history.push({
                      pathname: `/categories/${record.id}`,
                      state: { categories },
                    })
                  },
                  style: { cursor: 'pointer' }

                })}
                pagination={{
                  current: page,
                  defaultPageSize: pageSize,
                  total: categoriesCount,
                  onChange: this.nextPage,
                }}
              />
            </Col>
          </Row>
          {showNewCategoryModal &&
            ModalCreator({
              title: "Create Category",
              okType: "primary",
              okText: "Create",
              showModal: showNewCategoryModal,
              onOkay: this.createCategory,
              toggleModal: this.toggleCreateCategoryModal,
              inscription: (
                <NewCategoryUI
                  reference={this.reference}
                  departments={departments}
                  isCreatingCategory={isCreatingCategory}
                />
              ),
            })}
        </div>
      </Card>
    </>
    );
  }
}
